import ValidateModule from "../ValidateModule";

import type { IEventChannel } from "@esWebAPI/es/model/Models";
import type AjaxRequestBroker from "@esWebAPI/es/AjaxRequestBroker";
import type {ApiOpts} from "@esWebAPI/es/model/Models";
import type CacheModule from "../CacheModule";

const $ = global.$;
const es = global.es;

/**
 * @class SecurityAdminUsersGroupsModule
 */
export default class SecurityAdminUsersGroupsModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;

    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param resourceName 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        this.getPermissions = this.getPermissions.bind(this);
        this.getGroupPermissions = this.getGroupPermissions.bind(this);
        this.getGroupsAndPermissions = this.getGroupsAndPermissions.bind(this);
        this.createGroup = this.createGroup.bind(this);
        this.updateGroup = this.updateGroup.bind(this);
        this.removeGroup = this.removeGroup.bind(this);
        this.getUsers = this.getUsers.bind(this);
        this.createUser = this.createUser.bind(this);
        this.updateUser = this.updateUser.bind(this);
        this.removeUser = this.removeUser.bind(this);
        this.addAIChat = this.addAIChat.bind(this);
    }
    
    /**
     * Get all permissions assigned to tenant and may be used to assign permissions to any user inside this tenant
     * 
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    getPermissions(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/permissions";

        //return rqBroker.doGet({url: url});
        return this.cache.doGet(this.rqBroker, this.resourceName, url, "permissions", opts);
    }
    
    /**
     * getGroupPermissions
     * 
     * @param opts 
     * 
     * @returns 
     */
    getGroupPermissions(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/groups/permissions";
        //return rqBroker.doGet({url: url});
        return this.cache.doGet(this.rqBroker, this.resourceName, url, "groups_permissions", opts);
    }
    
    /**
     * Auxiliary method to get all the groups for tenant from the server, including the permissions associated to each one (built from es.api.admin.getGroups() and #getGroupPermissions())
     * 
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    getGroupsAndPermissions(opts?: ApiOpts) {
        return $.when(
            es.api.security.getGroups(opts),
            this.getGroupPermissions(opts)
        ).then((groups, permissions) => {
            const groupsAndPermissions = [];
            for (var i=0; i<groups.length; i++) {
                var groupPermissions = permissions.get('id', [groups[i].id]);

                //private user security group does not have permissions
                if (groupPermissions != null) {
                    groupsAndPermissions.push($.extend({}, groups[i], {'permissions': groupPermissions.permissions}));
                } else {
                    groupsAndPermissions.push($.extend({}, groups[i], {'permissions': []}));
                }
            }
            
            return groupsAndPermissions;
        });
    }
    
    /**
     * createGroup
     * 
     * @param group 
     * @param opts 
     * 
     * @returns 
     */
    createGroup(group: object, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(group)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName)+ '/group';
        
        return this.rqBroker.doPost({
            'url': url,
            opts: opts,
            'data': group
        }).done((rs) => {
            this.cache.clear("Security");
            this.cache.clear(this.resourceName);
            //this.eventChannel.fireEvent("onCreateGroup", rs);
        });
    }
    
    /**
     * updateGroup
     */
    updateGroup(group: object, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(group)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName)+ '/group';
        
        return this.rqBroker.doPut({
            'url': url,
            opts: opts,
            'data': group
        }).done((rs) => {
            this.cache.clear("Security");
            this.cache.clear(this.resourceName);
            //this.eventChannel.fireEvent("onCreateGroup", rs);
        });
    }
    
    /**
     * removeGroup
     * 
     * @param groupId 
     * @param opts 
     * 
     * @returns 
     */
    removeGroup(groupId: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(groupId)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName)+ '/group/' + groupId;
        
        return this.rqBroker.doDelete({
            'url': url,
            opts: opts
        }).done((rs) => {
            this.cache.clear("Security");
            this.cache.clear(this.resourceName);
            //eventChannel.fireEvent("onCreateGroup", rs);
        });
    }
    
    /**
     * Get all the users for tenant from the server
     * 
     * @param opts
     * 
     * @return a jquery deferred object resolved when the request has completed
     */
    getUsers(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/users";
        
        //return rqBroker.doGet({url: url});
        return this.cache.doGet(this.rqBroker, this.resourceName, url, "users", opts);
    }
    
    /**
     * Creates a user
     * 
     * @param user
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    createUser(user: object, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(user)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName)+ '/user';
        
        return this.rqBroker.doPost({
            'url': url,
            opts: opts,
            'data': user
        }).done((rs) => {
            this.cache.clear("Security");
            this.cache.clear(this.resourceName);
            //this.eventChannel.fireEvent("onCreateUser", rs);
        });
    }
    
    /**
     * Updates a user
     * 
     * @param user 
     * @param opts 
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    updateUser(user: object, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(user)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName)+ '/user/' + user.username;

        return this.rqBroker.doPut({
            'url': url,
            opts: opts,
            'data': user
        }).done((rs) => {
            this.cache.clear("Security");
            this.cache.clear(this.resourceName);
            //this.eventChannel.fireEvent("onUpdateUser", rs);
        });
    }
    
    /**
     * Removes a user
     * 
     * @param username
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    removeUser(username: string, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(username)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName)+ '/user/' + username;

        return this.rqBroker.doDelete({
            'url': url,
            opts: opts
        }).done(() => {
            this.cache.clear("Security");
            this.cache.clear(this.resourceName);
            //eventChannel.fireEvent("onRemoveUser", rs);
        });
    };

    /**
     * Admin user opt-in. Add AIChat to users group.
     *
     * @returns a dfd promise
     */
    addAIChat() {
        const url = es.api.getURL(this.resourceName) + "/add_ai_chat";
        return this.rqBroker.doPost({
            'url': url,
        }).done(() => {
            this.cache.clear("groups_permissions");
            this.cache.clear("Security");
            this.cache.clear(this.resourceName);
        });
    }
}
